/**
 * What the main bar offers, whatever it looks like (tiles, pills): the actions of the screen the
 * backend allows, "start over", the languages before one is chosen, or the
 * menu an action opened (its chips, "← Back" first: packs/menu.ts).
 */
import { START_SCREEN } from '../../core/wire';
import type { ActionDef, MenuChip, MenuEntry, MenuStep } from '../../packs/contract';
import { backMenu, openMenu, stepMenu } from '../../packs/menu';
import { allowedActions } from '../../packs/screen';
import { useApp } from '../../react/app';
import { useUiActions } from '../../shell/useUiActions';
import { useScreen, useSession, useTranslate, useUi } from '../../react/hooks';

export const RESTART: ActionDef = { id: 'restart', label: 'action.restart', icon: 'restart', tone: 'quiet' };

export type MainBarContent =
  | { kind: 'none' }
  | { kind: 'languages'; languages: string[]; choose(language: string): void }
  | { kind: 'actions'; actions: ActionDef[]; run(action: ActionDef): void }
  | { kind: 'menu'; entries: MenuEntry[]; nested: boolean; pick(chip: MenuChip): void; back(): void };

export function useMainBar(options: { withRestart?: boolean } = {}): MainBarContent {
  const app = useApp();
  const screen = useScreen();
  // Like the old kiosk: the language row shows until a language is chosen or the user has spoken, and
  // again after "start over" (askLanguage: the backend keeps the language chosen across a reset).
  const askLanguage = useUi((u) => u.askLanguage);
  const spoken = useSession((s) => (s?.turn_count ?? 0) > 0);
  const chosen = useSession((s) => s?.language_chosen ?? false);
  const languageChosen = spoken || (chosen && !askLanguage);
  const t = useTranslate();
  const actions = useUiActions();
  const menu = useUi((u) => u.menu);

  // Before there is a session (a replay at its first step) the kiosk is at its start too.
  const atStart = !screen.ctx || screen.name === START_SCREEN;
  if (atStart && !languageChosen && app.pack.languages.length > 1) {
    return {
      kind: 'languages',
      languages: app.pack.languages,
      choose: (lang) => {
        app.voice?.unlockAudio();
        app.ui.set((s) => ({ ...s, askLanguage: false }));
        void app.kiosk.setLanguage(lang);
      },
    };
  }
  if (!screen.ctx || !screen.def) return { kind: 'none' };
  const allowed = allowedActions(screen.def, screen.ctx);
  const ui = app.ui;
  const act = (step: MenuStep) => {
    app.voice?.unlockAudio();
    if ('press' in step) void app.kiosk.press(step.press.button, step.press.data ?? {});
    else if ('say' in step) void app.kiosk.say(step.say);
  };

  // the menu stays open while its action is still on offer
  const opened = menu ? allowed.find((a) => a.id === menu.action)?.menu : undefined;
  if (menu && opened) {
    return {
      kind: 'menu',
      entries: opened.view({ view: menu.view, values: menu.values }, screen.ctx),
      nested: menu.trail.length > 0,
      pick: (chip) => {
        const next = stepMenu(menu, chip.step);
        ui.set((s) => ({ ...s, menu: next.menu }));
        if (next.act) act(next.act);
      },
      back: () => ui.set((s) => ({ ...s, menu: s.menu && backMenu(s.menu) })),
    };
  }

  // as the old kiosk: "start over" on every screen, the start one too
  const restart = options.withRestart !== false ? [RESTART] : [];
  return {
    kind: 'actions',
    actions: [...allowed.filter((a) => a.place !== 'end'), ...restart, ...allowed.filter((a) => a.place === 'end')],
    run: (a) => {
      app.voice?.unlockAudio();
      const opens = a.menu;
      if (opens) ui.set((s) => ({ ...s, menu: openMenu(a.id, opens) }));
      else if (a.id === RESTART.id) actions.restart();
      else if (a.press) void app.kiosk.press(a.press.button, a.press.data ?? {});
      else if (a.say) void app.kiosk.say(t(a.say));
    },
  };
}
